import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContentCard } from "../../../components/ui";
import { fetchEmployeesByRole, getEmployee } from "../../../services/leadManagementService";
import { Field, ReadOnlyValue, SelectInput } from "./LeadForm";
import { leadKey } from "../leadForm";

/**
 * The five assignment fields, the field the lead names each assignee in, and
 * the canonical role each one is chosen from.
 */
export const ASSIGNMENT_FIELDS = [
  { key: "primarySalesExecutiveId", name: "primarySalesExecutiveName", role: "Sales_Executive" },
  { key: "secondarySalesExecutiveId", name: "secondarySalesExecutiveName", role: "Sales_Executive" },
  { key: "primaryTechnicianId", name: "primaryTechnicianName", role: "Technician" },
  { key: "salesSupportExecutiveId", name: "salesSupportExecutiveName", role: "Sales_Support_Executive" },
  { key: "serviceManagerId", name: "serviceManagerName", role: "Service_Manager" },
];

export const assignmentsOf = (lead) =>
  Object.fromEntries(ASSIGNMENT_FIELDS.map(({ key }) => [key, lead[key] == null ? "" : String(lead[key])]));

/** The Unassigned option's value; a select option can't have an empty value. */
const UNASSIGNED = "unassigned";

/**
 * The people a lead is assigned to. Setting the Primary Sales Executive is
 * what takes a lead out of the Unassigned queue. Saved with the page's Save.
 */
export function AssigningTechnician({ lead, values, editable, onChange }) {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState({});
  const [savedNames, setSavedNames] = useState({});
  const [error, setError] = useState("");

  // The lead names its assignees. One it doesn't name is looked up by id, or failing
  // that, in their role's list.
  useEffect(() => {
    const roles = new Map(
      ASSIGNMENT_FIELDS.filter(({ key, name }) => lead[key] && !lead[name]).map(({ key, role }) => [String(lead[key]), role])
    );
    roles.forEach((role, id) =>
      getEmployee(id)
        .catch(() => fetchEmployeesByRole(role).then((rows) => rows.find((row) => String(row.id) === id) ?? Promise.reject()))
        .then((employee) => setSavedNames((current) => ({ ...current, [id]: employee.name })))
        .catch(() => {})
    );
  }, [lead]);

  // A role's employees are read the first time one of its dropdowns is opened.
  const loadRole = (role) => {
    if (employees[role]) return;
    setEmployees((current) => ({ ...current, [role]: [] }));
    fetchEmployeesByRole(role)
      .then((rows) => setEmployees((current) => ({ ...current, [role]: rows })))
      .catch((loadError) => setError(loadError.message));
  };

  /** The saved assignee's name: as the lead gives it, or as looked up by id. */
  const savedName = (key, name) => lead[name] || savedNames[values[key]];

  // Unassigned comes first, so a role can be cleared.
  const optionsFor = (key, name, role) => [
    { value: UNASSIGNED, label: t("leadManagement.unassigned") },
    ...(employees[role]?.length
      ? employees[role].map((row) => ({ value: row.id, label: row.name }))
      : values[key]
        ? [{ value: values[key], label: savedName(key, name) }]
        : []),
  ];

  return (
    <ContentCard title={t("leadManagement.assignmentTitle")} bodyClassName="p-4 sm:p-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {ASSIGNMENT_FIELDS.map(({ key, name, role }) => (
          <Field key={key} label={t(leadKey("fields", key))} htmlFor={`lead-${key}`}>
            {editable ? (
              <SelectInput
                id={`lead-${key}`}
                value={values[key]}
                options={optionsFor(key, name, role)}
                placeholder={t("leadManagement.unassigned")}
                onOpen={() => loadRole(role)}
                onChange={(value) => onChange(key, value === UNASSIGNED ? "" : value)}
              />
            ) : (
              // Read-only: the assignee's name, or Unassigned when there is none.
              <ReadOnlyValue
                id={`lead-${key}`}
                value={savedName(key, name) || (values[key] ? "" : t("leadManagement.unassigned"))}
              />
            )}
          </Field>
        ))}
      </div>
      {error ? <p className="mt-3 text-[12px] font-semibold text-rose-600">{error}</p> : null}
    </ContentCard>
  );
}

export default AssigningTechnician;
